import { questionnaireSectionApi } from '@/lib/api/questionnaireApi';
import { handleQueryError } from '@/lib/queryErrorHandler';
import { SectionFormData } from '@/lib/sectionSchema';
import { QuestionnaireSectionProps } from '@/types/questionnaire';
import { router } from '@inertiajs/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

interface UseSmartSectionUpdateProps {
  section: QuestionnaireSectionProps;
  allSections: Array<{ id: number; section_order: number; [key: string]: any }>;
  onClose: () => void;
}

export function useSmartSectionUpdate({
  section,
  allSections,
  onClose,
}: UseSmartSectionUpdateProps) {
  const queryClient = useQueryClient();

  const updateSectionMutation = useMutation({
    mutationFn: async (data: SectionFormData) => {
      // Check if order changed
      if (data.section_order && data.section_order !== section.section_order) {
        // Calculate which sections need to be reordered
        const newOrder = data.section_order;
        const oldOrder = section.section_order;

        const reorderData: Array<{ id: number; section_order: number }> = [];

        // Add the section being updated
        reorderData.push({ id: section.id, section_order: newOrder });

        // Find sections that need to shift
        allSections.forEach((s) => {
          if (s.id === section.id) return;

          // If moving up (old > new), shift sections between new and old down
          if (
            oldOrder > newOrder &&
            s.section_order >= newOrder &&
            s.section_order < oldOrder
          ) {
            reorderData.push({ id: s.id, section_order: s.section_order + 1 });
          }
          // If moving down (old < new), shift sections between old and new up
          else if (
            oldOrder < newOrder &&
            s.section_order > oldOrder &&
            s.section_order <= newOrder
          ) {
            reorderData.push({ id: s.id, section_order: s.section_order - 1 });
          }
        });

        // Call reorder endpoint
        await questionnaireSectionApi.reorder(reorderData);

        // Update other fields if any changed
        const { section_order: _, ...otherData } = data;
        if (Object.keys(otherData).length > 0) {
          await questionnaireSectionApi.update(section.id, otherData);
        }
      } else {
        // No order change, just update normally
        await questionnaireSectionApi.update(section.id, data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['template', section.questionnaire_template_id],
      });
      queryClient.invalidateQueries({ queryKey: ['section', section.id] });
      router.reload({ only: ['template'] });
      onClose();
      toast.success('Section updated successfully');
    },
    onError: (error) =>
      handleQueryError(error, 'Failed to update section. Please try again.'),
  });

  const handleSubmit = (data: SectionFormData) => {
    updateSectionMutation.mutate(data);
  };

  return {
    updateSectionMutation,
    handleSubmit,
  };
}
